// Onboarding — first-run intake. 6 fields, then preview brain set, then complete.
// Mirrors screen-01-onboarding.html mockup.

import { useMemo, useState } from 'react';
import * as api from '../api';
import type { Brain, CompanyContext } from '../types';
import { ElementCell } from '../components/ElementCell';

interface Props {
  initialContext: CompanyContext | null;
  allBrains: Brain[];
  onComplete: (savedContext: CompanyContext) => void;
}

export function Onboarding({ initialContext, allBrains, onComplete }: Props) {
  const [orgName, setOrgName] = useState(initialContext?.org_name ?? '');
  const [industry, setIndustry] = useState(initialContext?.industry ?? '');
  const [role, setRole] = useState(initialContext?.role ?? '');
  const [focusAreas, setFocusAreas] = useState<string[]>(parseChips(initialContext?.focus_areas));
  const [keyTerms, setKeyTerms] = useState<string[]>(parseChips(initialContext?.key_terms));
  const [freeText, setFreeText] = useState(initialContext?.free_text ?? '');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live preview: which brains would auto-select for the current intake?
  // We mirror the keyword scorer from app/api/src/brains.ts — simplified.
  const previewBrains = useMemo(
    () => previewSelection({ allBrains, focusAreas, keyTerms, industry, role, orgName, freeText }),
    [allBrains, focusAreas, keyTerms, industry, role, orgName, freeText],
  );

  async function handleSubmit() {
    setPending(true);
    setError(null);
    try {
      const saved = await api.updateCompanyContext({
        org_name: orgName || null,
        industry: industry || null,
        role: role || null,
        focus_areas: focusAreas.join(','),
        key_terms: keyTerms.join(','),
        free_text: freeText || null,
      });
      if (saved) onComplete(saved);
    } catch (err: any) {
      setError(err?.message ?? String(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <header className="sticky top-0 z-10 bg-bg/85 backdrop-blur-md border-b border-hairline px-4 sm:px-6 md:px-8 py-3.5 flex justify-between items-center gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <ElementCell symbol="Sc" atomicNumber={21} />
          <span className="font-display text-base text-ink whitespace-nowrap">
            <span className="text-accent">ELEMENT</span>USA
          </span>
          <span className="hidden sm:inline text-text2 text-xs font-medium uppercase tracking-wide14 pl-3.5 border-l border-border whitespace-nowrap">
            Element IQ
          </span>
        </div>
        <div className="font-mono text-[10px] sm:text-[11px] text-text2 bg-surface border border-hairline px-2.5 sm:px-3 py-1.5 rounded-full whitespace-nowrap shrink-0">
          Step <span className="text-accent font-semibold">1</span><span className="hidden sm:inline"> of 2 · Welcome</span>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8 py-8 md:py-14 pb-24 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-12">
        {/* INTAKE */}
        <div>
          <div className="font-display text-[11px] text-text3 uppercase tracking-wide14 mb-2.5 inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-accent rounded-full" />
            Welcome
          </div>
          <h1 className="font-display text-[26px] sm:text-[32px] md:text-[38px] leading-[1.1] text-ink tracking-tight25 mb-3">
            Tell us about your work, <span className="text-accent">we'll do the rest.</span>
          </h1>
          <p className="text-base text-text2 mb-8 max-w-[540px] leading-relaxed">
            Element IQ loads the right domain expertise based on what you're working on. Six fields, sixty seconds — then we pick the brains for your first chat.
          </p>

          <div className="bg-surface border border-border rounded-lg p-5 sm:p-7 md:p-8 shadow-md">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-6">
              <Field label="Organization">
                <Input value={orgName} onChange={setOrgName} placeholder="ElementUSA" />
              </Field>
              <Field label="Industry / Domain">
                <Input value={industry} onChange={setIndustry} placeholder="Critical Minerals" />
              </Field>
            </div>

            <Field label="Your role" help="Used to tune answer style — engineers get flowsheets, executives get summaries.">
              <Input value={role} onChange={setRole} placeholder="Process Engineer" />
            </Field>

            <Field label="What you're working on" help={<>Press <strong className="text-ink font-semibold">Enter</strong> to add. We use these to choose which domain brains to load by default.</>}>
              <ChipInput chips={focusAreas} onChange={setFocusAreas} placeholder="Add a focus area…" />
            </Field>

            <Field label="Vocabulary you use" help="Acronyms, internal codenames, project names — anything that's specific to your work. Helps us route questions to the right brain.">
              <ChipInput chips={keyTerms} onChange={setKeyTerms} placeholder="Add a term…" />
            </Field>

            <Field label="Tell us more about your company" help={<>Lives in your settings. Editable any time. <strong className="text-ink font-semibold">Powers brain auto-selection and lens-mode answers.</strong></>}>
              <textarea
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="Long-form is fine — paste an internal one-pager, copy a Slack channel description, anything that captures what you actually do."
                className="w-full bg-bgsoft border border-hairline rounded-md px-3.5 py-2.5 font-sans text-sm text-ink placeholder:text-text3 outline-none transition-all focus:bg-surface focus:border-ink focus:shadow-[0_0_0_3px_rgba(29,29,29,0.06)] min-h-[100px] leading-relaxed"
              />
            </Field>

            <div className="flex justify-between items-center mt-8 pt-6 border-t border-hairline">
              <button
                onClick={() => onComplete({} as CompanyContext)}
                className="text-text2 text-[13px] no-underline border-b border-dashed border-border pb-px hover:text-ink hover:border-ink"
              >
                Skip — set up later
              </button>
              <button
                onClick={handleSubmit}
                disabled={pending}
                className="bg-ink text-bg border-0 px-5 py-3 rounded-md font-sans text-sm font-semibold cursor-pointer inline-flex items-center gap-2 transition-all shadow-sm hover:bg-accent hover:-translate-y-px hover:shadow-md disabled:opacity-50"
              >
                {pending ? 'Saving…' : 'See my brain set'}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </button>
            </div>
            {error && <div className="mt-4 text-sm text-accent">{error}</div>}
          </div>
        </div>

        {/* PREVIEW */}
        <div>
          <div className="bg-surface border border-border rounded-lg p-5 sm:p-7 lg:sticky lg:top-24 shadow-sm">
            <div className="font-display text-[10px] text-text3 uppercase tracking-wide14 mb-2">Live preview</div>
            <div className="font-display text-lg text-ink mb-1.5">Based on your context, we'd load</div>
            <p className="text-[13px] text-text2 mb-6 leading-relaxed">
              These are the domain brains Element IQ would auto-select for your first chat. You can change this anytime — turn it off in settings or override in any conversation.
            </p>

            <div className="font-display text-[10px] text-text2 uppercase tracking-wide12 mb-3">
              {previewBrains.length} brain{previewBrains.length === 1 ? '' : 's'} active
            </div>
            <div className="flex flex-col gap-2 mb-1">
              {previewBrains.length === 0 ? (
                <div className="text-text3 text-[13px] italic py-6 text-center leading-relaxed">
                  Add some focus areas above to see which brains we'd load.
                </div>
              ) : (
                previewBrains.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 px-3 py-2.5 bg-bgsoft border border-hairline rounded-md">
                    <ElementCell symbol={b.symbol} atomicNumber={b.atomic_number} size="sm" />
                    <div className="flex flex-col leading-snug min-w-0 flex-1">
                      <span className="text-[13px] font-medium text-ink truncate">{b.name}</span>
                      <span className="font-mono text-[10px] text-text3 mt-0.5">{b.org} · v{b.version} · {b.author}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({ label, help, children }: { label: string; help?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mb-6 last:mb-0">
      <label className="block font-display text-[11px] text-ink uppercase tracking-wide12 mb-2">{label}</label>
      {children}
      {help && <div className="text-xs text-text2 mt-1.5 leading-snug">{help}</div>}
    </div>
  );
}

function Input({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-bgsoft border border-hairline rounded-md px-3.5 py-2.5 font-sans text-sm text-ink placeholder:text-text3 outline-none transition-all focus:bg-surface focus:border-ink focus:shadow-[0_0_0_3px_rgba(29,29,29,0.06)]"
    />
  );
}

function ChipInput({ chips, onChange, placeholder }: { chips: string[]; onChange: (chips: string[]) => void; placeholder?: string }) {
  const [draft, setDraft] = useState('');
  function commit() {
    const v = draft.trim();
    if (!v) return;
    if (!chips.includes(v)) onChange([...chips, v]);
    setDraft('');
  }
  function remove(c: string) {
    onChange(chips.filter((x) => x !== c));
  }
  return (
    <div className="flex flex-wrap gap-1.5 items-center bg-bgsoft border border-hairline rounded-md px-2.5 py-2 min-h-[44px] transition-all focus-within:bg-surface focus-within:border-ink focus-within:shadow-[0_0_0_3px_rgba(29,29,29,0.06)]">
      {chips.map((c) => (
        <span key={c} className="bg-ink text-bg font-mono text-[11px] px-2.5 py-0.5 rounded inline-flex items-center gap-1.5">
          {c}
          <button onClick={() => remove(c)} className="text-accent font-bold leading-none cursor-pointer">×</button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Backspace' && draft === '' && chips.length > 0) {
            onChange(chips.slice(0, -1));
          }
        }}
        onBlur={commit}
        placeholder={chips.length === 0 ? placeholder : 'Add another…'}
        className="flex-1 border-none outline-none bg-transparent font-sans text-[13px] text-ink min-w-[100px] py-1 placeholder:text-text3"
      />
    </div>
  );
}

function parseChips(s: string | null | undefined): string[] {
  if (!s) return [];
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

/**
 * Mirror of the API's keyword scorer (app/api/src/brains.ts).
 * Returns the top brains that match the current intake — for live preview.
 */
function previewSelection({
  allBrains,
  focusAreas,
  keyTerms,
  industry,
  role,
  orgName,
  freeText,
}: {
  allBrains: Brain[];
  focusAreas: string[];
  keyTerms: string[];
  industry: string;
  role: string;
  orgName: string;
  freeText: string;
}): Brain[] {
  const STOP = new Set(['the','a','an','and','or','of','to','in','on','at','by','for','with','from','as','is','are','what','how','why','this','that']);
  const blob = [...focusAreas, ...keyTerms, industry, role, orgName, freeText].join(' ').toLowerCase();
  const tokens = Array.from(new Set(
    blob.replace(/[^a-z0-9\s-]/g, ' ').split(/\s+/).filter((t) => t.length >= 2 && !STOP.has(t)),
  ));
  if (tokens.length === 0) return [];

  const scored = allBrains.map((b) => {
    const tagSet = new Set(b.tags.toLowerCase().split(',').map((t) => t.trim()).filter(Boolean));
    const desc = b.description.toLowerCase();
    let score = 0;
    for (const kw of tokens) {
      if (tagSet.has(kw)) score += 3;
      if (desc.includes(kw)) score += 2;
    }
    return { brain: b, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored.filter((s) => s.score > 0).slice(0, 5).map((s) => s.brain);
}
